import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Chip,
  Drawer,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  PowerSettingsNew as PowerIcon,
  Clear as ClearIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';
import api from '../utils/api';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import '../pages/client/client-nav-css/ClientNavigation-light.css';
import '../pages/client/client-nav-css/ClientNavigation-dark.css';

// Navigation item interface
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

// TopBar component
const TopBar: React.FC<{
  onMenuToggle: () => void;
  unreadCount: number;
  user: any;
  onNotificationClick: (event: React.MouseEvent<HTMLElement>) => void;
  onLogout: () => void;
  isDarkMode: boolean;
  onThemeToggle: () => void;
  isTemporaryAccess: boolean;
  companySlug: string | null;
  currentPath: string;
}> = ({ onMenuToggle, unreadCount, user, onNotificationClick, onLogout, isDarkMode, onThemeToggle, isTemporaryAccess, companySlug, currentPath }) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const theme = useTheme();

  return (
    <div className={`client-nav-topbar ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Left: Mobile Menu + Client Name (Super Admin Viewer only) */}
      <div className="client-nav-topbar-left">
        {isMobile && (
          <button
            className="client-nav-mobile-menu-btn"
            onClick={onMenuToggle}
            aria-label="open drawer"
          >
            <MenuIcon />
          </button>
        )}
        
        {/* Show Client Name ONLY when Super Admin is viewing as client */}
        {(user?.isSuperAdminViewer === true || 
          user?.role === 'super_admin_viewer' || 
          isTemporaryAccess || 
          currentPath.includes('super-admin-viewer')) && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.primary,
              display: { xs: 'none', sm: 'block' },
              ml: 2
            }}
          >
            {`Viewing Client: ${user?.companyName || companySlug || user?.username || 'Unknown'}`}
          </Typography>
        )}
      </div>

      {/* Right: Theme, Notifications, User */}
      <div className="client-nav-topbar-right">
        {/* Theme Toggle */}
        <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
          <div
            className="client-nav-theme-toggle"
            onClick={onThemeToggle}
          >
            {/* Sun Icon - Left Side */}
            <img
              src="/super-admin/sun-icon.svg"
              alt="Light Mode"
              className={`client-nav-theme-icon sun ${isDarkMode ? 'inactive' : 'active'}`}
            />
            
            {/* Moon Icon - Right Side */}
            <img
              src="/super-admin/moon-icon.svg"
              alt="Dark Mode"
              className={`client-nav-theme-icon moon ${isDarkMode ? 'active' : 'inactive'}`}
            />
      
            {/* Toggle Switch */}
            <div className="client-nav-theme-switch" />
          </div>
        </Tooltip>

        {/* Notifications - Only show for regular users, not super admin viewer */}
        {!isTemporaryAccess && !user?.isSuperAdminViewer && (
          <Tooltip title="Notifications">
            <button
              className="client-nav-notification-btn"
              onClick={onNotificationClick}
            >
              <Badge badgeContent={unreadCount} color="error">
                <img
                  src="/super-admin/notification-bell.svg"
                  alt="Notifications"
                  width="24"
                  height="26"
                  style={{
                    filter: isDarkMode 
                      ? 'brightness(0) invert(1)' 
                      : 'brightness(0) saturate(100%) invert(15%) sepia(100%) saturate(2000%) hue-rotate(200deg) brightness(0.7) contrast(1.2)',
                    transition: 'filter 0.3s ease',
                  }}
                />
              </Badge>
            </button>
          </Tooltip>
        )}

        {/* User Info */}
        <div className="client-nav-user-info">
          <div className="client-nav-username">
            {user?.isSuperAdminViewer 
              ? `Super Admin - ${user?.adminUsername || 'Admin'}`
              : `${user?.username || 'Client'}`
            }
          </div>
          <div className="client-nav-role">
            {user?.isSuperAdminViewer 
              ? 'Viewer Access'
              : user?.role === 'admin' 
                ? 'Client Admin' 
                : user?.role === 'editor' 
                  ? 'Client Sales' 
                  : 'Client Viewer'
            }
          </div>
        </div>
        
        {/* Logout */}
        <Tooltip title={isTemporaryAccess ? "Close" : "Logout"}>
          <button
            className="client-nav-logout-btn"
            onClick={onLogout}
          >
            <PowerIcon />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

// NavItem component
const NavItemComponent: React.FC<{
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isDarkMode: boolean;
}> = ({ item, isActive, onClick, isDarkMode }) => {
  
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };
  
  return (
    <div
      className={`client-nav-item ${isActive ? 'active' : ''} ${isActive && !isDarkMode ? 'light-mode-override' : ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="menuitem"
      aria-current={isActive ? "page" : undefined}
    >
      <div className="client-nav-item-icon">
        {item.icon}
      </div>
      <div className="client-nav-item-text">
        {item.label}
      </div>
    </div>
  );
};

// SideNav component
const SideNav: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  user: any;
  isDarkMode: boolean;
  companySlug: string | null;
  isTemporaryAccess: boolean;
  onLogout: () => void;
  companyFeatures?: any;
  featuresLoading?: boolean;
}> = ({ isOpen, onClose, currentPath, onNavigate, user, isDarkMode, companySlug, isTemporaryAccess, onLogout, companyFeatures, featuresLoading }) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');

  // Define navigation items based on user role and access type
  const getNavItems = (): NavItem[] => {
    // Check if this is super admin viewer
    const isSuperAdminViewer = currentPath.includes('super-admin-viewer');
    const basePath = isSuperAdminViewer 
      ? `/${companySlug}/${user?.username}/super-admin-viewer`
      : `/${companySlug}/${user?.username}`;

    const baseItems: NavItem[] = [
      { 
        label: 'Dashboard', 
        path: companySlug 
          ? `${basePath}/dashboard`
          : '/client-signup',
        icon: <DashboardIcon /> 
      },
    ];

    // Add User Status for Client Admin (not temporary access) or for super admin viewer
    if ((user?.role === 'admin' && !isTemporaryAccess) || isSuperAdminViewer) {
      baseItems.push({
        label: 'User Status',
        path: companySlug 
          ? `${basePath}/user-status`
          : '/client-signup',
        icon: <PeopleIcon />
      });
    }

    // Add Assigned Tasks for all users except temporary access (unless super admin viewer)
    if (!isTemporaryAccess || isSuperAdminViewer) {
      baseItems.push({
        label: 'Assigned Tasks',
        path: companySlug 
          ? `${basePath}/assigned-tasks`
          : '/client-signup',
        icon: <AssignmentIcon />
      });
    }

    // Add CRM/Mailforge tab for all users (admin, editor, viewer) - only if CRM feature is enabled
    // Wait for features to load before showing/hiding CRM to prevent flash
    // Only show CRM if features are loaded AND CRM is enabled
    if (!featuresLoading) {
      // Check if CRM feature is enabled (default to true if not set, only if features are loaded)
      const crmEnabled = companyFeatures?.crm !== false && (user?.companyFeatures?.crm !== false);
      if ((!isTemporaryAccess || isSuperAdminViewer) && crmEnabled) {
        // For editors, show as "Mailforge" and use /mailforge path, for others show as "CRM" with /crm path
        const label = user?.role === 'editor' ? 'Mailforge' : 'CRM';
        const crmPath = user?.role === 'editor' ? 'mailforge' : 'crm';
        baseItems.push({
          label: label,
          path: companySlug 
            ? `${basePath}/${crmPath}`
            : '/client-signup',
          icon: <EmailIcon />
        });
      }
    }

    return baseItems;
  };

  const navItems = getNavItems();

  const drawerContent = (
    <div className={`client-nav-sidebar ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Logo */}
      <div className="client-nav-logo-container">
        <img
          src={isDarkMode ? "/super-admin/leadforge-dark-logo.svg" : "/super-admin/leadforge-light-logo.svg"}
          alt="LeadForge"
          className="client-nav-logo"
        />
      </div>


      {/* Main Navigation */}
      <div className="client-nav-main">
        <nav aria-label="Primary">
          {navItems.map((item) => (
            <NavItemComponent
              key={item.path}
              item={item}
              isActive={currentPath === item.path || currentPath.startsWith(item.path)}
              onClick={() => {
                onNavigate(item.path);
                if (isMobile) onClose();
              }}
              isDarkMode={isDarkMode}
            />
          ))}
        </nav>
      </div>

      {/* Footer Actions - Logout */}
      <div className="client-nav-footer">
        <nav aria-label="Footer">
          <NavItemComponent
            item={{
              label: isTemporaryAccess ? "Close" : "Logout",
              path: "/logout",
              icon: <PowerIcon />
            }}
            isActive={false}
            onClick={() => {
              onLogout();
              if (isMobile) onClose();
            }}
            isDarkMode={isDarkMode}
          />
        </nav>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <div className={`client-nav-sidebar ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {drawerContent}
    </div>
  );
};

// Main ClientLayoutNew component
const ClientLayoutNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const { user, logout } = useAuth();
  const isTemporaryAccess = Boolean(window.location.search.includes('temp_access=true') || user?.isSuperAdminViewer);
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchor, setNotificationAnchor] = useState<HTMLElement | null>(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [companyFeatures, setCompanyFeatures] = useState<any>(null);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  // Removed topbar date display, no need to track current date

  // Get company slug from current URL
  const getCompanySlug = () => {
    const pathParts = window.location.pathname.split('/');
    const companySlug = pathParts[1];
    
    // Only return if it's a valid company slug (not 'admin', 'client', etc.)
    if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
      return companySlug;
    }
    return null;
  };

  const companySlug = getCompanySlug();

  // EXPLICIT SAFEGUARD: Prevent any automatic notification popup opening
  useEffect(() => {
    if (isNotificationOpen && !notificationAnchor) {
      setIsNotificationOpen(false);
    }
  }, [isNotificationOpen, notificationAnchor]);

  // Initialize companyFeatures from user object if available (from login response)
  useEffect(() => {
    if (user?.companyFeatures) {
      setCompanyFeatures(user.companyFeatures);
      setFeaturesLoading(false);
    } else if (user) {
      setFeaturesLoading(true);
    } else {
      setFeaturesLoading(false);
    }
  }, [user?.companyFeatures]);

  // Fetch company features on mount - MUST complete before rendering navigation
  useEffect(() => {
    const fetchCompanyFeatures = async () => {
      // If we already have features from user object, skip fetch
      if (user?.companyFeatures) {
        return;
      }

      setFeaturesLoading(true);
      try {
        // Fetch from dashboard endpoint
        const response = await api.get('/api/client/dashboard');
        if (response.data.success && response.data.data.companyFeatures) {
          setCompanyFeatures(response.data.data.companyFeatures);
        } else {
          // Default to CRM enabled if not set
          setCompanyFeatures({ crm: true });
        }
      } catch (error) {
        console.error('Error fetching company features:', error);
        // Default to CRM enabled on error
        setCompanyFeatures({ crm: true });
      } finally {
        setFeaturesLoading(false);
      }
    };

    if (user && !user.companyFeatures) {
      fetchCompanyFeatures();
    } else if (!user) {
      setFeaturesLoading(false);
    }
  }, [user]);

  // Fetch assignment notifications on mount and periodically
  useEffect(() => {
    // Skip notifications for temporary access (super admin viewer)
    if (!isTemporaryAccess && !user?.isSuperAdminViewer) {
      fetchNotifications();
      
      // Fetch notifications every 30 seconds
      const notificationInterval = setInterval(fetchNotifications, 30000);
      
      return () => {
        clearInterval(notificationInterval);
      };
    }
  }, [isTemporaryAccess, user?.isSuperAdminViewer]);

  // Removed date updater as date is no longer shown in topbar

  // Fetch assignment notifications
  const fetchNotifications = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/assignments/notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Handle the new simplified response format
      if (response.data.success && response.data.data) {
        setNotifications(response.data.data.notifications || []);
        setUnreadCount(response.data.data.unreadCount || 0);
      } else {
        // Fallback for old format or errors
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching assignment notifications:', error);
      // Set safe defaults on error
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    setIsNotificationOpen(true);
    
    // Mark all notifications as read when bell is clicked
    if (unreadCount > 0) {
      markAllAsRead();
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
    setIsNotificationOpen(false);
  };

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${getApiUrl()}/api/assignments/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications(); // Refresh notifications
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${getApiUrl()}/api/assignments/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications(); // Refresh notifications
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Handle notification item click
  const handleNotificationItemClick = (notification: any) => {
    // Mark this specific notification as read
    markAsRead(notification.id);
    
    // Navigate to assigned tasks tab with current assignments tab (tab 1)
    if (companySlug) {
      navigate(`/${companySlug}/${user?.username}/assigned-tasks?tab=1`);
    } else {
      navigate('/client-signup');
    }
    
    // Close the notification popup
    handleNotificationClose();
  };

  const handleLogout = () => {
    if (isTemporaryAccess) {
      // For temporary access, just remove the tokens and close the window
      sessionStorage.removeItem('temp_client_token');
      sessionStorage.removeItem('temp_client_username');
      window.close();
    } else {
      logout();
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  // Format notification timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`client-nav-root ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Sidebar */}
      <SideNav
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
        onNavigate={handleNavigate}
        user={user}
        isDarkMode={isDarkMode}
        companySlug={companySlug}
        isTemporaryAccess={isTemporaryAccess}
        onLogout={handleLogout}
        companyFeatures={companyFeatures}
        featuresLoading={featuresLoading}
      />

      {/* Main Content */}
      <div className="client-nav-content">
        {/* Top Bar */}
        <TopBar
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          unreadCount={unreadCount}
          user={user}
          onNotificationClick={handleNotificationClick}
          onLogout={handleLogout}
          isDarkMode={isDarkMode}
          onThemeToggle={toggleTheme}
          isTemporaryAccess={isTemporaryAccess}
          companySlug={companySlug}
          currentPath={location.pathname}
        />

        {/* Content Area */}
        <div className={`client-nav-main-content ${isDarkMode ? 'client-nav-main-content-dark' : 'client-nav-main-content-light'}`}>
          <Outlet />
        </div>
      </div>

      {/* Notifications Popover */}
      <Popover
        open={isNotificationOpen}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 400,
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}`,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: isDarkMode ? '#ffffff' : '#000000' }}>
              Assignment Notifications
            </Typography>
            <IconButton onClick={handleNotificationClose} size="small">
              <ClearIcon />
            </IconButton>
          </Box>
          
          {!notifications || notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No notifications yet
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationItemClick(notification)}
                    sx={{
                      backgroundColor: notification.read 
                        ? 'transparent' 
                        : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: notification.read ? 'normal' : 'bold', color: isDarkMode ? '#ffffff' : '#000000' }}>
                            {notification.message}
                          </Typography>
                          {!notification.read && (
                            <Chip size="small" label="New" color="error" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(notification.timestamp)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {notifications && index < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </div>
  );
};

export default ClientLayoutNew;
